import { Injectable, WritableSignal, effect, inject, signal } from '@angular/core';
import { ApiService } from '@app/shared/services/api.service';
import { signOut } from 'aws-amplify/auth';
import { CryptoCipherService } from '../services/crypto-cipher.service';
import { ConstantVariables } from '@app/shared/util/constant-variables';
import { lastValueFrom } from 'rxjs';
import { environment } from '@environments/environment';

@Injectable({
  providedIn: 'root'
})
export class Utilities {
  cryptoService = inject(CryptoCipherService);
  apiService = inject(ApiService);
  constantVariables = inject(ConstantVariables);
  currentUserSession = signal<any>('');
  institutionDetails = signal<any>('');
  userDetails$ = signal<any>('');
  recordsPerPage = signal<number>(10);
  saveSuccessPopup$ = signal<string>('');
  saveIntakeSuccessPopup$ = signal<string>('');
  saveCourseSuccessPopup$ = signal<string>('');
  saveCourseInstanceSuccessPopup$ = signal<string>('');
  saveErSuccessPopup$ = signal<string>('');
  saveDeptSuccessPopup = signal<string>('');
  isDialogOpen$ = signal<boolean>(false);
  studentTypeToggleRestrict$ = signal<any>(null); // type {val: any, info: boolean, from: any} | null
  ckEditorVal: any;
  institutionMsgState = signal<{ msg: string, msgType: 'success' | 'warning' | '', show: boolean }>({ msg: '', msgType: '', show: false });
  nonEduOrgMsgState$: WritableSignal<{ msg: string, msgType: 'success' | 'warning' | '', show: boolean }> = signal({ msg: '', msgType: '', show: false });
  assignedCourseInstancePopup = signal<boolean>(false);
  quickSearchDetails = signal<any>('');
  institutionRankingCategoryNames = signal<any>([]);
  courseDetailsForER$: WritableSignal<{ courseType: string, courseId: string, instanceId: string, instanceTitle: string, courseTitle: string, academicYear: string, pageType: string, courseQualification: string }> = signal({ courseType: '', courseId: '', instanceId: '', instanceTitle: '', courseTitle: '', academicYear: '', pageType: '', courseQualification: '' });
  institutionChangedInOtherTab = signal<{ institutionId: number, institutionName: string, instPageUrl: string } | null>(null);
  private currentInstitutionId: number | null = null;

  constructor() {
    // Capture the current institution ID on init
    try {
      const stored = localStorage.getItem('institutionDetails');
      if (stored) this.currentInstitutionId = JSON.parse(stored)?.institutionId ?? null;
    } catch (e) { /* ignore */
      console.error('Error while fetching institution details:', e);
      throw e;
    }
    this.institutionDetailsStorageListener();
  }

  institutionDetailsStorageListener() {
    // Listen for localStorage changes from other tabs
    window.addEventListener('storage', (event: StorageEvent) => {
      if (event.key === 'institutionDetails' && event.newValue)
        try {
          const newDetails = JSON.parse(event.newValue);
          this.institutionDetails.set(event.newValue);

          // Only trigger popup if institution actually changed
          if (this.currentInstitutionId && Number(this.currentInstitutionId) !== Number(newDetails?.institutionId))
            this.institutionChangedInOtherTab.set({
              institutionId: newDetails?.institutionId,
              institutionName: newDetails?.institutionName,
              instPageUrl: ((this.getStoredUserDetails()?.roleName === 'SCC View Only' || newDetails?.status?.toLowerCase() === 'archived') ? '/view-institution/' : '/edit-institution/') + newDetails?.institutionId,
            });
          this.currentInstitutionId = newDetails?.institutionId;
        } catch (e) {
          // Ignore parse errors
          console.error('Error in event listener:', e);
          throw e;
        }
    });
  }

  userSignOut() {
    signOut().then(() => {
      localStorage.clear();
      this.userDetails$.set('');
    });
  }

  getInstituionDetails() {
    if (!this.institutionDetails())
      this.institutionDetails.set(localStorage.getItem("institutionDetails") ?? '')
    // Keep currentInstitutionId in sync whenever institution details are loaded
    try {
      const stored = localStorage.getItem('institutionDetails');
      if (stored) this.currentInstitutionId = JSON.parse(stored)?.institutionId ?? null;
    } catch (e) { /* ignore */
      console.error('Error in get institution details:', e);
      throw e;
    }
  }

  getUserDetails(email: string) {
    return this.apiService.getUserDetails(email);
  }

  getUserSession() {
    return this.currentUserSession()?.tokens?.idToken?.payload;
  }

  dataTypeOf(value: any) {
    return typeof value;
  }

  getStoredUserDetails() {
    try {
      const cipherText = localStorage.getItem("userDetails");
      const userDetails = cipherText ? this.cryptoService.decryptSecret(cipherText) : null;
      return userDetails ? JSON.parse(userDetails) : null;
    }
    catch (err) {
      this.userSignOut();
    }
  }

  setCKEditorDefaultConfig() {
    this.ckEditorVal = {
      toolbar: [
        [{ 'header': [1, 2, 3, false] }],

        ['bold', 'italic', 'underline', 'strike'],        // toggled buttons
        [{ 'script': 'sub' }, { 'script': 'super' }],      // superscript/subscript

        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        [{ 'indent': '-1' }, { 'indent': '+1' }],          // outdent/indent

        [{ 'size': ['small', false, 'large', 'huge'] }],  // custom dropdown

        ['link'],                         // link
        ['blockquote', 'code-block'],
        [{ 'align': [] }],
      ]
    }
  }

  clearStyleAttr(fieldId: any, formDetails: any) {
    if (this.constantVariables.ckEditorStyleRemoval.test(formDetails?.get(fieldId)?.value))
      formDetails.get(fieldId).setValue(formDetails.get(fieldId).value.replace(this.constantVariables.ckEditorStyleRemoval, ''));
  }

  decodeHTMLEntities(ckeditorVal: string): string {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = ckeditorVal;
    const result = textarea.value?.replaceAll('\xa0', ' ')?.replace(/<p><\/p>/g, '\r\n')?.replace(/<p>\s*<\/p>/g, '\r\n')?.replace(/\r?\n/g, '\r\n');
    return result?.replace(/<\/p>(?!$)/g, '</p>\r\n');
  }

  encodeToHTML(text: string): string {
    const returnText = text?.replace(/<\/p>\r\n/g, '</p>');
    return returnText?.replace(/\r\n/g, '<p></p>');
  }
  /**
   * Common method to encode CK Editor field values
   * @param fieldType - The type of the field
   * @param value - The value to potentially encode
   * @returns Encoded value if CK_EDITOR field with content, otherwise original value
   */
  encodeCkEditorField(fieldType: string, value: any): any {
    if (fieldType === 'CK_EDITOR' && value)
      return this.encodeToHTML(value);
    return value;
  }

  downloadFile(preSignedUrl: string, fileName: string) {
    const link = document.createElement('a');
    link.href = preSignedUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  async fetchFeatureFlagDetails(token: string, projectId: string, featureFlagId: string) {
    let currentUserData = null;
    if (this.currentUserSession() != '' && Object.keys(this.currentUserSession()).length != 0)
      currentUserData = environment.authDisable ? this.currentUserSession()?.getIdToken()?.decodePayload()?.email : this.currentUserSession()?.tokens?.idToken?.payload?.email;
    try {
      const flagResponse: any = await lastValueFrom(this.apiService.getFeatureFlag(token, projectId, featureFlagId));
      const userIdDetails = flagResponse?.strategies[0]?.parameters?.userIds?.split(',');
      const envScope: boolean = flagResponse?.strategies[0]?.scopes.some((scope: any) => scope?.environment_scope === environment.environmentName)
      if ((!userIdDetails && flagResponse?.active && envScope) || (flagResponse?.active && userIdDetails.includes(String(currentUserData)) && envScope))
        return true;
      return false;
    } catch (err) {
      console.log('fetch feature flag error', err);
      return false;
    }
  }

  removeCountryInstitutionDetails() {
    localStorage.removeItem('institutionDetails');
    localStorage.removeItem('countryDetails');
    this.institutionDetails.set('');
  }

  formatNotes(notes: string): string {
    return notes?.replace(/<\/p>\r\n/g, '</p>')?.replace(/\r\n/g, '<br>') || '';
  }

  onQuillCreated(quill: any) {
    quill.legacyGetSemanticHTML = quill.getSemanticHTML;
    quill.getSemanticHTML = (a: number, b: number) =>
      quill.legacyGetSemanticHTML(a, b)
        .replaceAll(/((?:&nbsp;)*)&nbsp;/g, '$1 ')
        .replaceAll(/((?:&amp;)*)&amp;/g, '$1&')
        .replaceAll(/((?:&lt;)*)&lt;/g, '$1<')
        .replaceAll(/((?:&gt;)*)&gt;/g, '$1>')
        .replaceAll(/((?:&#39;)*)&#39;/g, "$1'")
        .replaceAll(/((?:&quot;)*)&quot;/g, '$1"')
  }
}
